import {
  PatientOverview,
  PatientQueryParamProps,
  PatientQueryParams,
  SharedStateProvider,
  getValidPatientTab,
} from "@metriport/shared-internal";
import { useParams, useSearchParams } from "react-router-dom";
import { getOSSBaseURL } from "../../../api/apiOSS";
import { isApiKeyGenerated } from "../../../domain/api-key";
import { useAppContext } from "../../contexts/app";
import { useIsUserInSandbox } from "../../shared/useIsUserInSandbox";
import NoApiKey from "../no-api-key";

export default function PatientPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { patientId } = useParams();
  const { state } = useAppContext();
  const { isUserInSandbox } = useIsUserInSandbox(state);
  const isMetriportUser = !!state.user?.email?.endsWith("@metriport.com");
  const baseUrl = getOSSBaseURL(isUserInSandbox);

  if (!patientId || !state.isLoaded) {
    return null;
  }

  function setPatientQueryParams(update: Partial<PatientQueryParams>): void {
    const { patientTab, sectionName, sectionKey, resourceId } = update;
    setSearchParams(prevState => {
      if (patientTab) prevState.set("patientTab", patientTab);
      if (sectionName) prevState.set("sectionName", sectionName);
      if (sectionKey) prevState.set("sectionKey", sectionKey);
      if (resourceId) prevState.set("resourceId", resourceId);
      return prevState;
    });
  }

  const patientTab = getValidPatientTab(searchParams.get("patientTab"));
  const sectionName = searchParams.get("sectionName") ?? undefined;
  const sectionKey = searchParams.get("sectionKey") ?? undefined;
  const resourceId = searchParams.get("resourceId") ?? undefined;

  function clearPatientQueryParams(): void {
    setSearchParams({});
  }

  const patientQueryParams: PatientQueryParamProps = {
    patientTab,
    sectionName,
    sectionKey,
    resourceId,
    setPatientQueryParams,
    clearPatientQueryParams,
  };

  const apiKeyGenerated = isApiKeyGenerated(state.keyIds);

  if (!apiKeyGenerated) {
    return <NoApiKey />;
  }

  return (
    <SharedStateProvider
      authToken={state.authToken}
      baseUrl={baseUrl}
      ehrDetails={undefined}
      isMetriportUser={isMetriportUser}
      userEmail={state.user?.email}
      featureFlags={state.featureFlags}
    >
      <PatientOverview
        isImpersonating={state.isImpersonating}
        patientId={patientId}
        patientQueryParams={patientQueryParams}
      />
    </SharedStateProvider>
  );
}
